import React, { useContext } from "react";

import { Link, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { P } from "../../../atoms/Texts";

import { LinkButton, PrimaryButton } from "../../../atoms/Buttons";
import { CircleRadio, DarkFormField } from "../../../atoms/Inputs";
import { ReferralsCard } from "../../../atoms/Cards/card.referrals";
import ActionPageHero from "../../../molecules/ActionPageHero";

import { demoContext } from "../../../../shared/store/contexts";

import Joyride from "react-joyride";
import { useWalkthrough } from "../../../../shared/hooks/useWalkthrough";

const ReferralResults = ({ results, page, setPage, pageRange }) => (
  <>
    <div className="referral-card grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16 mt-6">
      {results?.map((referral) => (
        <ReferralsCard key={referral.id} referral={referral} />
      ))}
    </div>
    <div className="flex items-center justify-center mt-6">
      {pageRange.map((_, index) => (
        <LinkButton
          key={index}
          className="mr-4"
          disabled={page === index * 15}
          onClick={() => setPage(index * 15)}
        >
          {index + 1}
        </LinkButton>
      ))}
    </div>
  </>
);

const SearchForm = ({ onSubmit, register, errors, searchBy }) => {
  return (
    <form onSubmit={onSubmit} className="md:w-2/3">
      <div className="flex flex-wrap lg:flex-no-wrap">
        <div className="w-full flex-grow">
          <div>
            <DarkFormField
              type="search"
              id="query"
              name="query"
              className="py-1 flex w-full"
              placeholder="Search"
              data_testid="query-input"
              {...register("query", {
                required: "Please enter a search phrase",
              })}
            />
            <div className="flex items-center">
              <P className="mr-4" fontSize="14px">
                Search by:
              </P>
              <div className="flex items-center space-x-4">
                <CircleRadio
                  id="service-name"
                  name="searchBy"
                  value="service-name"
                  label="Service name"
                  fontSize="14px"
                  style={{ marginLeft: "8px" }}
                  checked={searchBy === "service-name"}
                  {...register("searchBy")}
                />
                <CircleRadio
                  id="user-name"
                  name="searchBy"
                  value="user-name"
                  label="User name"
                  fontSize="14px"
                  style={{ marginLeft: "8px" }}
                  checked={searchBy === "user-name"}
                  {...register("searchBy")}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="md:ml-4">
          <PrimaryButton>Search</PrimaryButton>
        </div>
      </div>
      {errors.query && (
        <P fontSize="12px" className="-mt-4 mb-4 text-red-400 text-sm">
          {errors.query.message}
        </P>
      )}
    </form>
  );
};

const ReferralsPage = () => {
  const { id } = useParams();
  const { referrals } = useContext(demoContext);
  const {
    register,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      searchBy: "service-name",
    },
  });

  const steps = [
    {
      target: ".create-referral",
      content: "Create a new referral form.",
    },
    {
      target: ".search-referral",
      content: "Quickly search through referrals.",
    },
    {
      target: ".referral-card",
      content: "This shows the details of each unique referrals.",
    },

    {
      target: ".referral-user",
      content: "This shows the details of the user who was referred.",
    },
    {
      target: ".referral-service",
      content: "This shows the service for which this referral was created.",
    },
    {
      target: ".referral-case",
      content: "This shows the case attached to this referral.",
    },
    {
      target: ".case-summary",
      content: "A summary of the what the case offers",
    },
    {
      target: ".referral-source",
      content: "Shows where the referral form data was collected",
    },
    {
      target: ".isEligible",
      content: "Shows the eligibility status of the user",
    },
    {
      target: ".case-status",
      content: "This shows if a case has been approved or rejected",
    },
    {
      target: ".accept-btn",
      content: "Click this button to follow the approval process.",
    },
    {
      target: ".reject-btn",
      content: "Click this button to follow the rejection process",
    },
  ];

  const { joyrideProps } = useWalkthrough({
    steps,
    walkthroughKey: "referral_page_data",
    pageSpecific: true, // Each page tracks separately
  });

  const searchByValue = watch("searchBy");

  const [page, setPage] = React.useState(0);
  const [pageRange] = React.useState([]);

  return (
    <>
      <Joyride
        key={joyrideProps.run ? "walkthrough-active" : "walkthrough-inactive"}
        {...joyrideProps}
      />
      <main className="flex flex-col w-full">
        <div className="flex justify-between items-center">
          <ActionPageHero
            className="organization-container"
            title="Referrals"
            action
            actionComponent={
              <Link to={`/dashboard/organization/${id}/referral-forms/add`}>
                <PrimaryButton className="create-referral">
                  Create Referral Form
                </PrimaryButton>
              </Link>
            }
            description="Manage all Referrals"
          />
        </div>

        <div className="organization-container my-6 search-referral">
          <SearchForm register={register} errors={errors} searchBy={searchByValue} />
        </div>

        <section className="organization-container pb-10 flex-grow flex flex-col">
          <ReferralResults
            results={referrals}
            page={page}
            setPage={setPage}
            pageRange={pageRange}
          />
        </section>
      </main>
    </>
  );
};

export default ReferralsPage;
